"use client";

import { lazy } from "react";
import type { ToolUIMeta, ToolUIRegistry } from "../../tool-renderer";

export const quickbaseToolRegistry = {
  quickbaseGetApp: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseGetAppToolView }) => ({
        default: QuickbaseGetAppToolView,
      }))
    ),
  },
  quickbaseGetTables: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseGetTablesToolView }) => ({
        default: QuickbaseGetTablesToolView,
      }))
    ),
  },
  quickbaseGetTable: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseGetTableToolView }) => ({
        default: QuickbaseGetTableToolView,
      }))
    ),
  },
  quickbaseGetTableRelationships: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseGetTableRelationshipsToolView }) => ({
        default: QuickbaseGetTableRelationshipsToolView,
      }))
    ),
  },
  quickbaseGetReports: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseGetReportsToolView }) => ({
        default: QuickbaseGetReportsToolView,
      }))
    ),
  },
  quickbaseGetReport: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseGetReportToolView }) => ({
        default: QuickbaseGetReportToolView,
      }))
    ),
  },
  quickbaseGetFields: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseGetFieldsToolView }) => ({
        default: QuickbaseGetFieldsToolView,
      }))
    ),
  },
  quickbaseGetField: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseGetFieldToolView }) => ({
        default: QuickbaseGetFieldToolView,
      }))
    ),
  },
  quickbaseQueryRecords: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseQueryRecordsToolView }) => ({
        default: QuickbaseQueryRecordsToolView,
      }))
    ),
  },
  quickbaseGetUsers: {
    kind: "step",
    category: "research",
    view: lazy(() =>
      import("./views").then(({ QuickbaseGetUsersToolView }) => ({
        default: QuickbaseGetUsersToolView,
      }))
    ),
  },
  quickbaseUpsertRecords: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ QuickbaseUpsertRecordsToolView }) => ({
        default: QuickbaseUpsertRecordsToolView,
      }))
    ),
  },
  quickbaseDeleteRecords: {
    kind: "step",
    category: "create",
    view: lazy(() =>
      import("./views").then(({ QuickbaseDeleteRecordsToolView }) => ({
        default: QuickbaseDeleteRecordsToolView,
      }))
    ),
  },
} satisfies ToolUIRegistry;

/**
 * Namespaced variant of `quickbaseToolRegistry` keyed by the
 * progressive-disclosure inner-tool name `quickbase_${toolName}`.
 *
 * Quickbase is a progressive-disclosure capability (ADR-057), so its tool calls
 * are dispatched through the `callCapabilityTool` proxy and every durable +
 * live-stream tool part is rewritten to the namespaced inner identity
 * `${capabilityId}_${toolName}` (e.g. `quickbase_quickbaseQueryRecords`). The
 * bare-name entries above stay in the static tier (still required by
 * `AllToolNames` and used by legacy persisted chats); these namespaced entries
 * reuse the same lazy views so the rich Quickbase cards render on the PD path
 * instead of falling through to the generic JSON fallback. Wired into the
 * built-in registry in `packages/sdk/react/src/tools/built-in-registry.ts`.
 */
export const quickbaseProgressiveDisclosureToolRegistry: Record<
  string,
  ToolUIMeta
> = Object.fromEntries(
  Object.entries(quickbaseToolRegistry).map(([name, meta]) => [
    `quickbase_${name}`,
    meta,
  ])
);
